import './MetadataTimelineRow.css';

import * as React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { TimelineCellConfig } from './getTimelineCellConfig';
import Button from '../common/Button';
import TimelineNode, { TimelineTranscriptionNode } from './TimelineNode';
import Spinner from '../common/Spinner';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';
import Store from '../store/Store';

const debug = require('../debug/debug')('wc:video:MetadataTimelineRow');

type Props = {
  data: Object,
  type: string,
  cellConfig: TimelineCellConfig,
  pps: number,
  loading: boolean,
  isExpanded: boolean,
  isDisabled?: boolean,
  isHovered: boolean,
  isClicked: Boolean,
  isMouseDown: boolean,
  isDrag: boolean,
  onMouseEnter: Function,
  onMouseLeave: Function,
  onRowClick: Function,
  onTimelineNodeMouseLeave: Function,
  onTimelineNodeMouseEnter: Function,
  onTimelineNodeMouseClick: Function,
  onAddClick: Function,
  getBoundaryOfRows: Function
};

type State = {
  left: number,
  top: number,
  lineLeft: number
};

export default class MetadataTimelineRow extends React.PureComponent<Props, State> {
  _node: ?HTMLDivElement;
  _timer: any;
  pointerWithinRange: boolean;

  state = {
    left: 0,
    top: 0,
    lineLeft: 0
  };

  componentDidUpdate(prevProps: Props) {
    if (this.isKeyframeMetadata()) {
      if (this.props.isHovered && !prevProps.isHovered) {
        this.addListeners();
      } else if (!this.props.isHovered && prevProps.isHovered) {
        this.removeListeners();
      }
    }
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  addListeners = () => {
    document.addEventListener('mousemove', this.onMouseMove);
  };

  removeListeners = () => {
    document.removeEventListener('mousemove', this.onMouseMove);
  };

  onMouseMove = (e: MouseEvent) => {
    const { minLeft, maxLeft } = this.props.getBoundaryOfRows();
    this.onMouseMoveDebounced(e.clientX, e.clientY, minLeft, maxLeft);
  };

  onMouseMoveDebounced = _.debounce(
    (x: number, y: number, minLeft: number, maxLeft: number) => {
      const offset = this._node ? this._node.getBoundingClientRect().left : 200;
      const top = this._node ? this._node.getBoundingClientRect().top : 131;
      this.pointerWithinRange = true;
      if (x < minLeft) {
        x = minLeft;
        this.pointerWithinRange = false;
      }
      if (x > maxLeft) {
        x = maxLeft;
        this.pointerWithinRange = false;
      }
      const left = x;
      this.setState({ left: left, top: top, lineLeft: x - offset - 10 });
    },
    100,
    { maxWait: 100 }
  );

  isKeyframeMetadata = () => {
    return this.props.data.isKeyFrameData;
  };

  renderCell = (t: number, i: number, step: number) => {
    return (
      <g key={i} className="cell">
        <line x1={i * step} y1={0} x2={i * step} y2="100%" />
      </g>
    );
  };

  isRenderAddDataPointBtn = () => {
    const { isMouseDown, isDrag, isHovered } = this.props;
    return this.isKeyframeMetadata() && isHovered && !isMouseDown && !isDrag && this.pointerWithinRange;
  };

  render() {
    const {
      data,
      type,
      cellConfig,
      pps,
      loading,
      isExpanded,
      isDisabled,
      isHovered,
      isClicked,
      onMouseEnter,
      onMouseLeave,
      onAddClick,
      onTimelineNodeMouseClick,
      onTimelineNodeMouseEnter,
      onTimelineNodeMouseLeave,
      onTimelineNodeMouseMove,
      onMouseDown,
      mouseMove,
      mouseUp,
      isMouseDown,
      isDrag
    } = this.props;
    //let datanew: Array = this.props.dataArray;
    // datanew.sort((a, b) => {
    //   a.startTimeMs - b.startTimeMs;
    // });
    if (isDisabled) {
      return <div name="MetadataTimelineRow" />;
    }

    const { left, top, lineLeft } = this.state;

    const Component = TimelineNode; // type === 'transcription' ? TimelineTranscriptionNode : TimelineNode;
    if (!Component) {
      const err = new Error(`No renderer found for type ${type}`);
      err.inputData = data;
      throw err;
    }

    const args = {
      type,
      color: data.color,
      pps,
      isExpanded,
      onClick: onTimelineNodeMouseClick,
      onMouseEnter: onTimelineNodeMouseEnter,
      onMouseLeave: onTimelineNodeMouseLeave,
      onMouseMove: onTimelineNodeMouseMove,
      onMouseDown,
      mouseMove,
      mouseUp
    };

    let nodes;
    if (loading) {
      nodes = null;
    } else {
      nodes = data.items.map((arr, i) => {
        const props = {
          ...args,
          key: arr._id,
          data: arr
        };

        return <Component {...props} />;
      });
    }

    return (
      <div
        name="MetadataTimelineRow"
        className={classNames({ hover: isHovered, clicked: isClicked })}
        style={{ height: isExpanded ? data.value.length * 36 : '' }}
        onMouseEnter={() => onMouseEnter(data)}
        onMouseLeave={onMouseLeave}
        onClick={(e: MouseEvent) => (e.ctrlKey || e.metaKey ? '' : this.props.onRowClick(data))}
        ref={(node) => (this._node = node)}>
        {this.isRenderAddDataPointBtn() ? (
          <div>
            <Button
              className="add"
              icon={ThemeSetup.getImageURL('plus-circle-black.svg')}
              onClick={(e) => onAddClick(e, this._node, data)}
              draggable={false}
              style={{ left, top }}
              title={t('ADD_DATA_TIMELINE_HERE')}
            />
            <svg style={{ left: left - 10, height: 36, pointerEvents: 'none', position: 'fixed', top: top }}>
              <g className="cellLine">
                <line x1="10" y1="0" x2="10" y2="100%" />
              </g>
            </svg>
          </div>
        ) : null}
        <svg>
          <g strokeWidth={1} stroke="#b5b5b5" fill="#b5b5b5">
            {loading ? <Spinner small /> : cellConfig.times.map((t, i) => this.renderCell(t, i, cellConfig.step))}
          </g>
          <g strokeWidth={2} stroke={data.color} fill={data.color}>
            {nodes}
          </g>
        </svg>
      </div>
    );
  }
}
